import React, { useState, useRef } from 'react';
import { useMutation, useQuery } from 'convex/react';
import { api } from '@convex/_generated/api';
import { useAppStore } from '@/lib/store';
import { isConvexEnabled } from '@/lib/convex';
import { formatIST, uid, getFileType, wordCount } from '@/utils/format';
import { fetchModels, ocrPDF } from '@/lib/ai/chat';
import { useBrainDoc } from '@hooks/useBrainDoc';

const WORKER_BASE = import.meta.env.VITE_WORKER_URL || 'https://palinos-ai-proxy.gnaneshbalusa016g.workers.dev';

const PROVIDERS = ['anthropic', 'openai', 'groq', 'openrouter', 'nvidia', 'gemini', 'mistral', 'deepseek', 'xai'];
const PROVIDER_LABELS = { anthropic: 'Anthropic', openai: 'OpenAI', groq: 'Groq', openrouter: 'OpenRouter', nvidia: 'NVIDIA', gemini: 'Google Gemini', mistral: 'Mistral AI', deepseek: 'DeepSeek', xai: 'xAI (Grok)' };

export default function CustomizeView() {
  const {
    customiseTab, setCustomiseTab,
    contextFiles, setContextFiles,
    apiKeys, setApiKeys,
    memories, setMemories, addMemory, openMemoryModal,
    availableModels, setAvailableModels,
    currentModel, setCurrentModel,
    setContextModal,
    userId,
    conversations, conversationMessages,
  } = useAppStore();
  const [scanning, setScanning] = useState(false);

  // Convex mutations + queries — always called unconditionally
  const convexUpsertApiKey        = useMutation(api.apiKeys.upsert);
  const convexRemoveApiKey        = useMutation(api.apiKeys.remove);
  const convexSetFallback         = useMutation(api.apiKeys.setFallback);
  const convexCreateContextFile   = useMutation(api.contextFiles.create);
  const convexDeleteContextFile   = useMutation(api.contextFiles.incrementDeleteCount);
  const convexCreateMemory        = useMutation(api.memories.create);
  const convexUpdateMemory        = useMutation(api.memories.update);
  const convexRemoveMemory        = useMutation(api.memories.remove);
  // All messages across conversations — used by scan (avoids the local-store gap)
  const convexAllMessages         = useQuery(api.messages.listRecentByUser, isConvexEnabled && userId ? { userId } : 'skip');

  // Inline memory edit state
  const { brainDoc, upsertBrainDoc } = useBrainDoc();
  const [brainDocDraft, setBrainDocDraft] = useState(null); // null = not editing
  const [savingBrainDoc, setSavingBrainDoc] = useState(false);
  const [showConvMemories, setShowConvMemories] = useState(false);

  const [editingMemId, setEditingMemId] = useState(null);
  const [editingMemText, setEditingMemText] = useState('');
  const [editingMemCat, setEditingMemCat] = useState('fact');

  // Scan all conversations through the worker AI and save extracted memories per conversation
  const scanConversations = async () => {
    // Prefer Convex messages (all history); fall back to local store for current session
    const allMsgs = convexAllMessages ?? [];
    const localMsgs = Object.entries(conversationMessages).flatMap(([convId, msgs]) =>
      (msgs || []).map((m) => ({ conversationId: convId, role: m.role, content: m.content || '' }))
    );
    // Merge: Convex first, then any local-only (conv_ IDs not in Convex)
    const convexIds = new Set(allMsgs.map((m) => m.conversationId));
    const merged = [...allMsgs, ...localMsgs.filter((m) => !convexIds.has(m.conversationId))];

    // Group by conversationId
    const byConv = {};
    for (const m of merged) {
      (byConv[m.conversationId] = byConv[m.conversationId] || []).push(m);
    }
    const convEntries = Object.entries(byConv).filter(([, msgs]) => msgs.length >= 2);

    if (convEntries.length === 0) {
      alert('No conversations to scan yet. Start a chat first, then scan!');
      return;
    }

    setScanning(true);
    let totalAdded = 0;
    try {
      const existing = [...useAppStore.getState().memories];

      for (const [convId, msgs] of convEntries) {
        const isRealConvId = convId && !convId.startsWith('conv_');

        // Build user/assistant pairs
        const pairs = [];
        for (let i = 0; i < msgs.length - 1; i++) {
          if (msgs[i].role === 'user' && msgs[i + 1]?.role === 'assistant') {
            pairs.push({ user: msgs[i].content || '', assistant: msgs[i + 1].content || '' });
          }
        }
        for (const pair of pairs.slice(-4)) {
          try {
            const resp = await fetch(`${WORKER_BASE}/api/memorize`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                messages: [
                  { role: 'user', content: pair.user.slice(0, 800) },
                  { role: 'assistant', content: pair.assistant.slice(0, 800) },
                ],
              }),
            });
            if (!resp.ok) continue;
            const data = await resp.json();
            const mems = Array.isArray(data.memories) ? data.memories : [];

            for (const m of mems) {
              if (!m.content || m.content.length < 5) continue;
              const dup = existing.some((e) =>
                e.content.toLowerCase().slice(0, 40) === m.content.toLowerCase().slice(0, 40)
              );
              if (dup) continue;
              const newMem = {
                id: uid(), content: m.content, category: m.category || 'fact',
                conversationId: isRealConvId ? convId : undefined,
                createdAt: Date.now(),
              };
              addMemory(newMem);
              existing.push(newMem);
              if (isConvexEnabled) {
                convexCreateMemory({
                  userId,
                  conversationId: isRealConvId ? convId : undefined,
                  content: m.content,
                  category: m.category || 'fact',
                }).catch(() => {});
              }
              totalAdded++;
            }
          } catch { /* skip failed pair */ }
        }
      }

      if (totalAdded === 0) {
        alert(`Scan complete — checked ${convEntries.length} conversation${convEntries.length !== 1 ? 's' : ''}. No new facts found (already up to date).`);
      } else {
        alert(`Saved ${totalAdded} new ${totalAdded === 1 ? 'memory' : 'memories'} from ${convEntries.length} conversation${convEntries.length !== 1 ? 's' : ''}.`);
      }
    } finally {
      setScanning(false);
    }
  };

  const deleteMemory = async (mem) => {
    setMemories(memories.filter((m) => (m.id ?? m._id) !== (mem.id ?? mem._id)));
    if (isConvexEnabled && mem._id) {
      convexRemoveMemory({ id: mem._id }).catch(console.error);
    }
  };

  const startEditMemory = (m) => {
    setEditingMemId(m.id ?? m._id);
    setEditingMemText(m.content);
    setEditingMemCat(m.category || 'fact');
  };

  const saveEditMemory = async (mem) => {
    const content = editingMemText.trim();
    if (!content) return;
    setMemories(memories.map((m) =>
      (m.id ?? m._id) === (mem.id ?? mem._id) ? { ...m, content, category: editingMemCat } : m
    ));
    if (isConvexEnabled && mem._id) {
      convexUpdateMemory({ id: mem._id, content, category: editingMemCat }).catch(console.error);
    }
    setEditingMemId(null);
  };

  const downloadMemories = () => {
    const data = JSON.stringify(memories.map((m) => ({
      content: m.content,
      category: m.category || 'fact',
      summary: m.summary || '',
      createdAt: m.createdAt,
    })), null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `palinos-memories-${Date.now()}.json`;
    a.click(); URL.revokeObjectURL(url);
  };

  const [uploading, setUploading] = useState(false);
  const memoryFileRef = React.useRef(null);

  const uploadMemories = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      if (!Array.isArray(parsed)) throw new Error('Invalid format — expected an array.');
      const existing = useAppStore.getState().memories;
      let added = 0;
      for (const item of parsed) {
        if (!item.content) continue;
        const dup = existing.some((m) => m.content.toLowerCase().slice(0, 40) === item.content.toLowerCase().slice(0, 40));
        if (!dup) {
          const newMem = { id: uid(), content: item.content, category: item.category || 'fact', summary: item.summary, createdAt: item.createdAt || Date.now() };
          addMemory(newMem);
          if (isConvexEnabled) {
            convexCreateMemory({ userId, content: item.content, category: item.category || 'fact', summary: item.summary }).catch(console.error);
          }
          added++;
        }
      }
      alert(`Imported ${added} new memories (${parsed.length - added} duplicates skipped).`);
    } catch (err) {
      alert('Import failed: ' + err.message);
    } finally {
      setUploading(false);
      e.target.value = null;
    }
  };

  const [newProvider, setNewProvider] = useState('');
  const [newApiKey, setNewApiKey] = useState('');
  const [newApiFallback, setNewApiFallback] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editInput, setEditInput] = useState('');
  const fileInputRef = useRef(null);

  // ─── Context File Handlers ────────────────────

  const saveContextFileToConvex = async (item) => {
    if (!isConvexEnabled) return null;
    try {
      return await convexCreateContextFile({
        userId, name: item.name, type: item.type,
        content: item.content ?? undefined, url: item.url ?? undefined,
        wordCount: item.wordCount, charCount: item.charCount,
      });
    } catch (e) { console.error('Failed to save context file:', e); return null; }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const type = getFileType(file.name);
    setOcrLoading(type === 'pdf');

    const addItem = (item) => {
      const { contextFiles: cur, setContextFiles: set } = useAppStore.getState();
      set([item, ...cur]);
    };
    const patchId = (localId, _id) => {
      const { contextFiles: cur, setContextFiles: set } = useAppStore.getState();
      set(cur.map((f) => f.id === localId ? { ...f, _id } : f));
    };

    if (type === 'pdf') {
      try {
        const { text, wordCount: wc, charCount } = await ocrPDF(file);
        const item = {
          id: uid(), name: file.name, type: 'text',
          content: text, url: null,
          wordCount: wc, charCount: charCount ?? text.length,
          uploadDate: Date.now(), createdAt: Date.now(), deletedCount: 0,
        };
        addItem(item);
        const _id = await saveContextFileToConvex(item);
        if (_id) patchId(item.id, _id);
      } catch {
        alert('PDF OCR failed. The file may not be supported.');
      } finally {
        setOcrLoading(false);
      }
    } else {
      const reader = new FileReader();
      reader.onload = async (ev) => {
        const text = ev.target.result;
        const item = {
          id: uid(), name: file.name, type,
          content: text, url: null,
          wordCount: wordCount(text), charCount: text.length,
          uploadDate: Date.now(), createdAt: Date.now(), deletedCount: 0,
        };
        addItem(item);
        const _id = await saveContextFileToConvex(item);
        if (_id) patchId(item.id, _id);
      };
      reader.readAsText(file);
    }
    e.target.value = null;
  };

  // 7-check deletion: increment counter in Convex (Convex removes at 7), update local state
  const deleteCtxFile = async (id) => {
    const file = contextFiles.find((f) => (f._id ?? f.id) === id);
    const newCount = (file?.deletedCount ?? 0) + 1;
    if (newCount >= 7) {
      setContextFiles(contextFiles.filter((f) => (f._id ?? f.id) !== id));
    } else {
      setContextFiles(contextFiles.map((f) => (f._id ?? f.id) === id ? { ...f, deletedCount: newCount } : f));
    }
    if (isConvexEnabled && file?._id) {
      try { await convexDeleteContextFile({ id: file._id }); } catch (e) { console.error(e); }
    }
  };

  // ─── API Key Handlers ─────────────────────────

  const handleAddApi = async () => {
    if (!newProvider || !newApiKey.trim()) return;
    setIsSaving(true);
    try {
      const provider = newProvider.toLowerCase();
      const preview = newApiKey.slice(0, 6) + '...' + newApiKey.slice(-4);
      const encoded = btoa(newApiKey);

      if (isConvexEnabled) {
        // Save to Convex — ConvexDataSync will sync back to Zustand automatically
        await convexUpsertApiKey({
          userId,
          provider,
          keyPreview: preview,
          keyEncrypted: encoded,
          isFallback: newApiFallback,
          isActive: true,
        });
      } else {
        // Local-only fallback
        setApiKeys([
          ...apiKeys,
          { id: uid(), provider, name: PROVIDER_LABELS[provider] ?? provider, keyPreview: preview, keyEncrypted: encoded, isVisible: false, isFallback: newApiFallback, isActive: true, deleteCount: 0, createdAt: Date.now() },
        ]);
      }

      setNewProvider(''); setNewApiKey(''); setNewApiFallback(false);
    } catch (err) {
      console.error('Failed to save API key:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const toggleVis = (id) => setApiKeys(apiKeys.map((a) => (a.id === id ? { ...a, isVisible: !a.isVisible } : a)));

  // 7-check deletion for API keys
  const deleteApi = async (id) => {
    const key = apiKeys.find((a) => (a._id ?? a.id) === id);
    const newCount = (key?.deleteCount ?? 0) + 1;
    if (newCount >= 7) {
      if (isConvexEnabled && key?._id) {
        try { await convexRemoveApiKey({ id: key._id }); } catch (e) { console.error(e); }
      }
      setApiKeys(apiKeys.filter((a) => (a._id ?? a.id) !== id));
    } else {
      setApiKeys(apiKeys.map((a) => (a._id ?? a.id) === id ? { ...a, deleteCount: newCount } : a));
    }
  };

  const toggleFallback = async (id) => {
    const key = apiKeys.find((a) => (a._id ?? a.id) === id);
    const newFallback = !key?.isFallback;
    setApiKeys(apiKeys.map((a) => (a._id ?? a.id) === id ? { ...a, isFallback: newFallback } : a));
    if (isConvexEnabled && key?._id) {
      try { await convexSetFallback({ id: key._id, isFallback: newFallback }); } catch (e) { console.error(e); }
    }
  };

  const startEdit = (id, currentEncrypted) => {
    setEditingId(id);
    setEditInput('');
  };

  const saveEdit = async (id) => {
    const newVal = editInput.trim();
    setEditingId(null);
    setEditInput('');
    if (!newVal) return;
    const key = apiKeys.find((a) => (a._id ?? a.id) === id);
    if (!key) return;
    const preview = newVal.slice(0, 6) + '...' + newVal.slice(-4);
    const encoded = btoa(newVal);
    // Optimistically update Zustand so chat picks it up immediately
    setApiKeys(apiKeys.map((a) =>
      (a._id ?? a.id) === id ? { ...a, keyEncrypted: encoded, keyPreview: preview, isVisible: false } : a
    ));
    if (isConvexEnabled && key?._id) {
      try {
        await convexUpsertApiKey({
          userId,
          provider: key.provider,
          keyPreview: preview,
          keyEncrypted: encoded,
          isFallback: key.isFallback ?? false,
          isActive: key.isActive ?? true,
        });
      } catch (e) { console.error(e); }
    }
  };

  // ─── Model grouping ───────────────────────────
  const modelsByProvider = availableModels.reduce((acc, m) => {
    (acc[m.provider] = acc[m.provider] || []).push(m);
    return acc;
  }, {});

  return (
    <div className="flex-1 flex flex-col p-4 md:p-6 overflow-y-auto w-full bg-[#232426]">
      <h1 className="text-white font-bold text-[18px] md:text-xl mb-6">Customise</h1>

      {/* Tabs */}
      <div className="flex bg-transparent border border-[#515153] rounded-[4px] overflow-hidden text-[13.5px] font-medium h-[30px] mb-8 w-fit">
        {['context', 'api', 'models', 'memory', 'other'].map((t) => (
          <button
            key={t}
            onClick={() => setCustomiseTab(t)}
            className={`px-5 h-full transition-colors flex items-center justify-center border-r border-[#515153] last:border-r-0 capitalize ${customiseTab === t ? 'bg-[#784A15] text-[#ED8B38]' : 'text-[#d5d6d9] hover:bg-[#323335]'}`}
          >
            {t}
          </button>
        ))}
      </div>

      {/* ═══ CONTEXT TAB ═══ */}
      {customiseTab === 'context' && (
        <div className="flex flex-col flex-1">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 gap-4 px-1">
            <div className="flex items-center gap-4 text-[14px]">
              <span className="font-bold text-white text-[15px]">{contextFiles.length} files</span>
              <span className="text-[#8f9196]">Total words: <span className="text-[#d5d6d9]">{contextFiles.reduce((a, c) => a + (c.wordCount || 0), 0)}</span></span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button onClick={() => setContextModal({ isOpen: true, mode: 'create_text', data: null })} className="bg-[#323335] border border-[#515153] text-[#d5d6d9] hover:bg-[#3e3f42] px-3.5 py-1.5 rounded flex items-center text-[13px] font-medium transition-colors">
                <svg className="w-3.5 h-3.5 mr-2 text-[#8f9196]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><line x1="12" y1="18" x2="12" y2="12" /><line x1="9" y1="15" x2="15" y2="15" /></svg>
                Create File
              </button>
              <button onClick={() => setContextModal({ isOpen: true, mode: 'create_link', data: null })} className="bg-[#323335] border border-[#515153] text-[#d5d6d9] hover:bg-[#3e3f42] px-3.5 py-1.5 rounded flex items-center text-[13px] font-medium transition-colors">
                <svg className="w-3.5 h-3.5 mr-2 text-[#8f9196]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></svg>
                Add Link
              </button>
              <label className={`bg-[#323335] hover:bg-[#3e3f42] border border-[#515153] text-white px-3.5 py-1.5 rounded flex items-center text-[13px] font-medium transition-colors cursor-pointer ${ocrLoading ? 'opacity-60 pointer-events-none' : ''}`}>
                {ocrLoading
                  ? <><svg className="w-3.5 h-3.5 mr-2 animate-spin text-[#6498CC]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4" /></svg>Processing PDF...</>
                  : <><svg className="w-3.5 h-3.5 mr-2 text-[#8f9196]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12" /></svg>Upload</>}
                <input type="file" className="hidden" ref={fileInputRef} onChange={handleUpload} accept=".pdf,.txt,.md" />
              </label>
            </div>
          </div>

          <div className="flex-1 overflow-x-auto bg-[#232426] border border-[#515153] rounded-md">
            <table className="w-full text-left text-[13px] whitespace-nowrap border-collapse min-w-[600px]">
              <thead className="bg-[#323335] text-[#8f9196] font-medium sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-3.5 border-b border-[#515153]">File Name</th>
                  <th className="px-3 py-3.5 border-b border-[#515153]">Type</th>
                  <th className="px-3 py-3.5 border-b border-[#515153]">Words</th>
                  <th className="px-5 py-3.5 border-b border-[#515153]">Uploaded (IST)</th>
                  <th className="px-5 py-3.5 text-right border-b border-[#515153]">Delete</th>
                </tr>
              </thead>
              <tbody className="text-[#d5d6d9]">
                {contextFiles.length === 0 ? (
                  <tr><td colSpan="5" className="px-5 py-8 text-center text-[#8f9196]">No files or links added yet.</td></tr>
                ) : contextFiles.map((item) => (
                  <tr key={item.id} className="hover:bg-[#323335] transition-colors group">
                    <td className="px-4 py-3 border-b border-[#515153]">
                      <span className="text-[#6498CC] hover:underline font-medium cursor-pointer truncate block max-w-[200px]">{item.name}</span>
                    </td>
                    <td className="px-3 py-3 border-b border-[#515153]">
                      <span className={`text-[11px] px-1.5 py-0.5 rounded font-bold uppercase ${item.type === 'pdf' ? 'bg-[#4c2424] text-[#e17171]' : item.type === 'link' ? 'bg-[#244c2a] text-[#71e184]' : 'bg-[#323335] text-[#d5d6d9]'}`}>
                        {item.type === 'link' ? 'URL' : item.type.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-3 py-3 border-b border-[#515153] text-[#8f9196]">{item.wordCount || 0}</td>
                    <td className="px-5 py-3 border-b border-[#515153] text-[#8f9196]">{formatIST(item.uploadDate ?? item.createdAt)}</td>
                    <td className="px-5 py-3 border-b border-[#515153] text-right">
                      <button
                        onClick={() => deleteCtxFile(item._id ?? item.id)}
                        className={`opacity-0 group-hover:opacity-100 px-2 py-0.5 rounded text-[11px] font-bold transition-all border ${
                          (item.deletedCount ?? 0) > 0
                            ? 'border-[#e17171] text-[#e17171] bg-[#4c2424] opacity-100'
                            : 'border-[#515153] text-[#8f9196] bg-[#323335] hover:border-[#e17171] hover:text-[#e17171]'
                        }`}
                      >
                        {(item.deletedCount ?? 0) > 0 ? `${item.deletedCount}/7 ×` : 'Delete'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-[#515153] mt-2 px-1">Click Delete 7 times to permanently remove a file. Progress resets on page reload.</p>
        </div>
      )}

      {/* ═══ API TAB ═══ */}
      {customiseTab === 'api' && (
        <div className="flex flex-col flex-1">
          <div className="mb-6 bg-[#232426] border border-[#515153] rounded-md p-4 max-w-4xl">
            <h3 className="text-white font-bold text-[15px] mb-4">Add API Key</h3>
            <div className="flex flex-wrap gap-4 items-end">
              <div className="flex-1 min-w-[160px]">
                <label className="block text-[#8f9196] text-[12px] mb-1.5 font-medium">Provider</label>
                <select value={newProvider} onChange={(e) => setNewProvider(e.target.value)} className="w-full bg-[#323335] border border-[#515153] rounded px-3 py-1.5 text-white text-[13px] outline-none focus:border-[#6498CC] appearance-none">
                  <option value="" disabled>Select Provider</option>
                  {PROVIDERS.map((p) => <option key={p} value={p}>{PROVIDER_LABELS[p]}</option>)}
                </select>
              </div>
              <div className="flex-[2] min-w-[280px]">
                <label className="block text-[#8f9196] text-[12px] mb-1.5 font-medium">API Key</label>
                <input value={newApiKey} onChange={(e) => setNewApiKey(e.target.value)} type="password" className="w-full bg-[#323335] border border-[#515153] rounded px-3 py-1.5 text-white text-[13px] outline-none focus:border-[#6498CC]" placeholder="sk-..." />
              </div>
              <div className="flex items-center gap-2 mb-1">
                <div className="w-[14px] h-[14px] rounded-[3px] border cursor-pointer flex items-center justify-center" style={{ backgroundColor: newApiFallback ? '#6498CC' : 'transparent', borderColor: newApiFallback ? '#6498CC' : '#515153' }} onClick={() => setNewApiFallback(!newApiFallback)}>
                  {newApiFallback && <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>}
                </div>
                <label onClick={() => setNewApiFallback(!newApiFallback)} className="text-[#d5d6d9] text-[13px] cursor-pointer select-none">Fallback key</label>
              </div>
              <button onClick={handleAddApi} disabled={isSaving || !newProvider || !newApiKey.trim()} className="bg-[#6498CC] hover:bg-[#5282b8] text-white px-5 py-1.5 rounded text-[13px] font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed">
                {isSaving ? 'Saving...' : 'Save Key'}
              </button>
            </div>
            <p className="text-[11px] text-[#515153] mt-3">Keys are base64-encoded in memory. Set a Fallback key to auto-switch if the primary key fails during a chat.</p>
          </div>

          <div className="flex-1 overflow-x-auto bg-[#232426] border border-[#515153] rounded-md">
            <table className="w-full text-left text-[13px] whitespace-nowrap border-collapse min-w-[600px]">
              <thead className="bg-[#323335] text-[#8f9196] font-medium sticky top-0">
                <tr>
                  <th className="px-5 py-3.5 border-b border-[#515153]">Provider</th>
                  <th className="px-3 py-3.5 border-b border-[#515153]">Key Preview</th>
                  <th className="px-3 py-3.5 border-b border-[#515153]">Role</th>
                  <th className="px-5 py-3.5 text-right border-b border-[#515153]">Actions</th>
                </tr>
              </thead>
              <tbody className="text-[#d5d6d9]">
                {apiKeys.length === 0 ? (
                  <tr><td colSpan="4" className="px-5 py-8 text-center text-[#8f9196]">No API keys added yet.</td></tr>
                ) : apiKeys.map((k) => {
                  const kId = k._id ?? k.id;
                  const isEditing = editingId === kId;
                  return (
                    <tr key={kId} className="hover:bg-[#323335] transition-colors group border-b border-[#515153]">
                      <td className="px-5 py-3 font-medium capitalize">{PROVIDER_LABELS[k.provider] ?? k.provider}</td>
                      <td className="px-3 py-3 font-mono text-[#8f9196]">
                        {isEditing ? (
                          <div className="flex items-center gap-2">
                            <input
                              autoFocus
                              type="password"
                              value={editInput}
                              onChange={(e) => setEditInput(e.target.value)}
                              onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(kId); if (e.key === 'Escape') { setEditingId(null); setEditInput(''); } }}
                              onBlur={() => saveEdit(kId)}
                              placeholder="Paste new key..."
                              className="bg-[#1d1e22] border border-[#6498CC] rounded px-2 py-0.5 text-white text-[12px] outline-none w-48"
                            />
                            <button onClick={() => saveEdit(kId)} className="text-[#71e184] hover:text-white text-[11px] font-bold px-1.5 py-0.5 rounded bg-[#244c2a]">Save</button>
                            <button onClick={() => { setEditingId(null); setEditInput(''); }} className="text-[#8f9196] hover:text-white text-[11px]">✕</button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <span>{k.isVisible ? atob(k.keyEncrypted ?? '') : k.keyPreview ?? '••••••••'}</span>
                            <button onClick={() => toggleVis(kId)} className="text-[#8f9196] hover:text-white p-1">
                              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                            </button>
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        <button onClick={() => toggleFallback(kId)} className={`px-2 py-0.5 rounded text-[11px] font-bold uppercase transition-all ${k.isFallback ? 'bg-[#4c2424] text-[#e17171]' : 'bg-[#244c2a] text-[#71e184]'}`}>
                          {k.isFallback ? 'Fallback' : 'Primary'}
                        </button>
                      </td>
                      <td className="px-5 py-3 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          {!isEditing && (
                            <button
                              onClick={() => startEdit(kId)}
                              className="opacity-0 group-hover:opacity-100 p-1 text-[#8f9196] hover:text-[#6498CC] transition-all"
                              title="Edit key"
                            >
                              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                            </button>
                          )}
                          <button
                            onClick={() => deleteApi(kId)}
                            className={`px-2.5 py-1 rounded text-[12px] font-medium transition-all border opacity-0 group-hover:opacity-100 ${
                              (k.deleteCount ?? 0) > 0 ? 'border-[#e17171] text-[#e17171] bg-[#4c2424] opacity-100' : 'border-[#515153] text-[#8f9196] bg-[#323335] hover:border-[#e17171] hover:text-[#e17171]'
                            }`}
                          >
                            {(k.deleteCount ?? 0) > 0 ? `${k.deleteCount}/7 ×` : 'Remove'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ═══ MODELS TAB ═══ */}
      {customiseTab === 'models' && (
        <div className="flex flex-col gap-8">
          <div className="flex items-center justify-between">
            <p className="text-[#8f9196] text-[13px]">
              Active model: <strong className="text-white">{availableModels.find((m) => m.id === currentModel)?.name ?? currentModel}</strong>
            </p>
            <span className="text-[11px] text-[#515153]">{availableModels.length} models across {Object.keys(modelsByProvider).length} providers</span>
          </div>

          {availableModels.length === 0 && (
            <div className="flex items-center gap-3 text-[#8f9196] text-[13px] py-8">
              <svg className="w-4 h-4 animate-spin text-[#6498CC]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4" /></svg>
              Loading models...
            </div>
          )}

          {Object.entries(modelsByProvider).map(([provider, models]) => {
            const hasKey = apiKeys.some((k) => k.provider === provider && k.isActive);
            const selectedInProvider = models.some((m) => m.id === currentModel);
            return (
              <div key={provider}>
                <div className="flex items-center gap-3 mb-3">
                  <h3 className="text-[13px] font-semibold text-white uppercase tracking-wider">{PROVIDER_LABELS[provider] ?? provider}</h3>
                  <span className="text-[10px] text-[#8f9196] bg-[#323335] border border-[#515153] px-1.5 py-0.5 rounded">{models.length} models</span>
                  {hasKey && <span className="text-[10px] text-[#71e184] bg-[#244c2a] px-1.5 py-0.5 rounded font-bold">KEY SET</span>}
                  {!hasKey && <span className="text-[10px] text-[#8f9196] px-1.5 py-0.5 rounded">no key</span>}
                  {selectedInProvider && <span className="text-[10px] text-[#6498CC] font-bold">● active</span>}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                  {models.map((m) => (
                    <button
                      key={m.id}
                      onClick={() => setCurrentModel(m.id)}
                      className={`p-4 rounded-md border text-left transition-all ${m.id === currentModel ? 'border-[#6498CC] bg-[#6498CC] bg-opacity-10 shadow-[0_0_12px_#6498CC33]' : 'border-[#515153] bg-[#323335] hover:border-[#6498CC] hover:bg-[#3e3f42]'}`}
                    >
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-white font-bold text-[13px] truncate pr-2">{m.name}</span>
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded flex-shrink-0 ${m.free ? 'bg-[#244c2a] text-[#71e184]' : 'bg-[#1e3a5f] text-[#6498CC]'}`}>
                          {m.free ? 'FREE' : 'PAID'}
                        </span>
                      </div>
                      <div className="text-[#8f9196] text-[12px] leading-relaxed">{m.description}</div>
                      <div className="text-[#515153] text-[10px] mt-2 font-mono truncate">{m.apiModel}</div>
                      {m.id === currentModel && (
                        <div className="mt-2 text-[10px] text-[#6498CC] font-bold flex items-center gap-1">
                          <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>
                          Selected
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ═══ MEMORY TAB ═══ */}
      {customiseTab === 'memory' && (() => {
        const catColors = {
          identity: 'bg-[#1e3a5f] text-[#6498CC]',
          name:     'bg-[#1e3a5f] text-[#6498CC]',
          role:     'bg-[#244c2a] text-[#71e184]',
          preference:'bg-[#1e3a3a] text-[#67e8f9]',
          project:  'bg-[#2a1e3a] text-[#c084fc]',
          decision: 'bg-[#3a2a1e] text-[#f59e0b]',
          task:     'bg-[#1e2a3a] text-[#93c5fd]',
          context:  'bg-[#3a2a1e] text-[#e8a87c]',
          location: 'bg-[#3a2a1e] text-[#e8a87c]',
          fact:     'bg-[#3e3f42] text-[#8f9196]',
        };

        // Group memories by conversationId (null/undefined → 'global')
        const groups = {};
        for (const m of memories) {
          const key = m.conversationId || 'global';
          if (!groups[key]) groups[key] = [];
          groups[key].push(m);
        }

        // Sort each group newest first
        Object.values(groups).forEach((g) => g.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0)));

        // Sort group keys: most recently updated first
        const sortedKeys = Object.keys(groups).sort((a, b) => {
          const latestA = Math.max(...groups[a].map((m) => m.createdAt || 0));
          const latestB = Math.max(...groups[b].map((m) => m.createdAt || 0));
          return latestB - latestA;
        });

        const convTitle = (convId) => {
          if (convId === 'global') return 'General';
          const c = conversations.find((c) => (c._id ?? c.id) === convId);
          return c?.title || 'Untitled Chat';
        };
        const convDate = (convId) => {
          if (convId === 'global') return '';
          const c = conversations.find((c) => (c._id ?? c.id) === convId);
          if (!c?.createdAt) return '';
          return new Date(c.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
        };

        // Brain doc word count
        const bdWords = brainDoc?.content ? brainDoc.content.trim().split(/\s+/).filter(Boolean).length : 0;
        const bdUpdated = brainDoc?.updatedAt ? new Date(brainDoc.updatedAt).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : null;

        return (
          <div className="flex flex-col gap-4">
            <input type="file" accept=".json" ref={memoryFileRef} className="hidden" onChange={uploadMemories} />

            {/* ── BRAIN DOC (primary memory file) ── */}
            <div className="bg-[#232426] border border-[#515153] rounded-lg overflow-hidden">
              <div className="flex items-center justify-between px-4 py-2.5 bg-[#2b2c2f] border-b border-[#515153]">
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-[#6498CC]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2z"/><path d="M12 8v4l3 3"/></svg>
                  <span className="text-white font-bold text-[14px]">Memory File</span>
                  <span className="text-[10px] text-[#8f9196] bg-[#323335] border border-[#515153] px-1.5 py-0.5 rounded">injected into every response</span>
                </div>
                <div className="flex items-center gap-2">
                  {bdWords > 0 && <span className="text-[11px] text-[#515153]">{bdWords} words</span>}
                  {bdUpdated && <span className="text-[11px] text-[#515153]">· {bdUpdated}</span>}
                  {brainDocDraft === null ? (
                    <button
                      onClick={() => setBrainDocDraft(brainDoc?.content || '')}
                      className="flex items-center gap-1 px-2.5 py-1 bg-[#323335] border border-[#515153] hover:border-[#6498CC] rounded text-[11px] text-[#d5d6d9] transition-colors"
                    >
                      <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                      Edit
                    </button>
                  ) : (
                    <div className="flex items-center gap-1.5">
                      <button
                        disabled={savingBrainDoc}
                        onClick={async () => {
                          setSavingBrainDoc(true);
                          try { await upsertBrainDoc({ userId, content: brainDocDraft }); } catch {}
                          setSavingBrainDoc(false);
                          setBrainDocDraft(null);
                        }}
                        className="px-2.5 py-1 rounded text-[11px] font-bold bg-[#6498CC] text-white hover:bg-[#5282b8] transition-colors disabled:opacity-50"
                      >{savingBrainDoc ? 'Saving…' : 'Save'}</button>
                      <button onClick={() => setBrainDocDraft(null)} className="px-2 py-1 rounded text-[11px] text-[#8f9196] hover:text-white bg-[#323335] border border-[#515153]">Cancel</button>
                    </div>
                  )}
                </div>
              </div>

              {brainDocDraft !== null ? (
                <textarea
                  autoFocus
                  value={brainDocDraft}
                  onChange={(e) => setBrainDocDraft(e.target.value)}
                  className="w-full bg-[#1d1e22] px-4 py-3 text-[#d5d6d9] text-[13px] leading-relaxed font-mono outline-none resize-none border-0"
                  style={{ minHeight: '320px' }}
                  placeholder="Memory file is empty. Start chatting — memories are added automatically after each response."
                />
              ) : brainDoc?.content ? (
                <pre className="px-4 py-3 text-[#d5d6d9] text-[13px] leading-relaxed font-mono whitespace-pre-wrap break-words overflow-auto" style={{ maxHeight: '480px' }}>
                  {brainDoc.content}
                </pre>
              ) : (
                <div className="flex flex-col items-center justify-center py-10 text-center px-4">
                  <svg className="w-8 h-8 mb-3 text-[#515153]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2z"/><path d="M12 8v4l3 3"/></svg>
                  <p className="text-[#8f9196] text-[13px] mb-1">No memory yet</p>
                  <p className="text-[#515153] text-[12px] max-w-sm">Palinos automatically builds a memory file as you chat. Every response adds or refines facts about you and your work.</p>
                </div>
              )}
            </div>

            {/* ── ACTIONS ROW ── */}
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <span className="text-[#8f9196] text-[12px]">{memories.length} structured {memories.length === 1 ? 'memory' : 'memories'} · {sortedKeys.length} {sortedKeys.length === 1 ? 'conversation' : 'conversations'}</span>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <button onClick={downloadMemories} disabled={memories.length === 0} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#323335] border border-[#515153] hover:border-[#6498CC] rounded text-[12px] font-medium text-[#d5d6d9] transition-colors disabled:opacity-40">
                  <svg className="w-3.5 h-3.5 text-[#6498CC]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                  Export
                </button>
                <button onClick={() => memoryFileRef.current?.click()} disabled={uploading} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#323335] border border-[#515153] hover:border-[#6498CC] rounded text-[12px] font-medium text-[#d5d6d9] transition-colors disabled:opacity-50">
                  {uploading
                    ? <><svg className="w-3.5 h-3.5 animate-spin text-[#6498CC]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4"/></svg>Importing...</>
                    : <><svg className="w-3.5 h-3.5 text-[#6498CC]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>Import</>
                  }
                </button>
                <button onClick={scanConversations} disabled={scanning} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#323335] border border-[#515153] hover:border-[#6498CC] rounded text-[12px] font-medium text-[#d5d6d9] transition-colors disabled:opacity-50">
                  {scanning
                    ? <><svg className="w-3.5 h-3.5 animate-spin text-[#6498CC]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4" /></svg>Scanning...</>
                    : <><svg className="w-3.5 h-3.5 text-[#6498CC]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>Scan conversations</>
                  }
                </button>
              </div>
            </div>

            {/* ── CONVERSATION MEMORIES (collapsible) ── */}
            {memories.length > 0 && (
              <div className="bg-[#232426] border border-[#515153] rounded-lg overflow-hidden">
                <button
                  onClick={() => setShowConvMemories((v) => !v)}
                  className="w-full flex items-center justify-between px-4 py-2.5 bg-[#2b2c2f] border-b border-[#515153] hover:bg-[#323335] transition-colors text-left"
                >
                  <div className="flex items-center gap-2">
                    <svg className="w-3.5 h-3.5 text-[#8f9196]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>
                    <span className="text-[#d5d6d9] text-[13px] font-semibold">Conversation Memories</span>
                    <span className="text-[10px] text-[#8f9196] bg-[#323335] border border-[#3e3f42] px-1.5 py-0.5 rounded">{memories.length}</span>
                  </div>
                  <svg className={`w-4 h-4 text-[#515153] transition-transform ${showConvMemories ? 'rotate-180' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9"/></svg>
                </button>

                {showConvMemories && (
                  <div className="flex flex-col gap-3 p-3">
                    {sortedKeys.map((convId) => (
                      <div key={convId} className="bg-[#1d1e22] border border-[#3e3f42] rounded-md overflow-hidden">
                        {/* Conversation sub-header */}
                        <div className="flex items-center justify-between px-3 py-2 border-b border-[#3e3f42]">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="text-[#8f9196] text-[12px] font-medium truncate">{convTitle(convId)}</span>
                            {convDate(convId) && <span className="text-[#515153] text-[11px] flex-shrink-0">{convDate(convId)}</span>}
                          </div>
                          <span className="text-[10px] text-[#8f9196] bg-[#323335] border border-[#515153] px-1.5 py-0.5 rounded flex-shrink-0 ml-2">
                            {groups[convId].length}
                          </span>
                        </div>
                        {/* Memory cards */}
                        <div className="flex flex-col divide-y divide-[#3e3f42]">
                          {groups[convId].map((m) => {
                            const mId = m.id ?? m._id;
                            const isEditing = editingMemId === mId;
                            const cc = catColors[isEditing ? editingMemCat : (m.category || 'fact')] || catColors.fact;
                            return (
                              <div key={mId} className="flex items-start gap-3 px-3 py-2.5 group hover:bg-[#232426] transition-colors">
                            {/* Category badge — clickable dropdown when editing */}
                            {isEditing ? (
                              <select
                                value={editingMemCat}
                                onChange={(e) => setEditingMemCat(e.target.value)}
                                className="text-[9px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wide mt-0.5 flex-shrink-0 bg-[#1d1e22] border border-[#6498CC] text-[#6498CC] outline-none cursor-pointer"
                              >
                                {['identity','preference','project','decision','task','context','fact'].map((c) => (
                                  <option key={c} value={c}>{c}</option>
                                ))}
                              </select>
                            ) : (
                              <span className={`${cc} text-[9px] px-1.5 py-0.5 rounded font-bold uppercase mt-0.5 flex-shrink-0 tracking-wide`}>{m.category || 'fact'}</span>
                            )}

                            <div className="flex-1 min-w-0">
                              {isEditing ? (
                                <textarea
                                  autoFocus
                                  value={editingMemText}
                                  onChange={(e) => setEditingMemText(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); saveEditMemory(m); }
                                    if (e.key === 'Escape') setEditingMemId(null);
                                  }}
                                  rows={3}
                                  className="w-full bg-[#1d1e22] border border-[#6498CC] rounded px-2 py-1.5 text-[#d5d6d9] text-[13px] leading-relaxed outline-none resize-none"
                                />
                              ) : (
                                <p className="text-[#d5d6d9] text-[13px] leading-relaxed">{m.content}</p>
                              )}
                              {!isEditing && m.createdAt && (
                                <p className="text-[#515153] text-[11px] mt-0.5">
                                  {new Date(m.createdAt).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                </p>
                              )}
                            </div>

                            <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-all">
                              {isEditing ? (
                                <>
                                  <button
                                    onClick={() => saveEditMemory(m)}
                                    className="px-2 py-0.5 rounded text-[11px] font-bold bg-[#6498CC] text-white hover:bg-[#5282b8] transition-colors"
                                    title="Save (Enter)"
                                  >Save</button>
                                  <button
                                    onClick={() => setEditingMemId(null)}
                                    className="px-1.5 py-0.5 rounded text-[11px] text-[#8f9196] hover:text-white bg-[#323335] border border-[#515153]"
                                  >✕</button>
                                </>
                              ) : (
                                <>
                                  <button onClick={() => startEditMemory(m)} className="text-[#8f9196] hover:text-[#6498CC] p-1" title="Edit memory">
                                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                  </button>
                                  <button onClick={() => deleteMemory(m)} className="text-[#8f9196] hover:text-[#e17171] p-1" title="Remove memory">
                                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  })()}

      {/* ═══ OTHER TAB ═══ */}
      {customiseTab === 'other' && (
        <div className="flex-1 flex flex-col gap-4 max-w-xl">
          <h3 className="text-white font-bold text-[15px]">Other Settings</h3>
          <div className="bg-[#323335] border border-[#515153] rounded-md p-4 text-[13px] text-[#8f9196]">
            Additional configuration options will appear here in future updates.
          </div>
        </div>
      )}
    </div>
  );
}
